import React from "react";
import { useLang } from "@/lib/i18n";
import { Kicker, SectionTitle, Reveal } from "./Section";
import { ArrowUpRight } from "lucide-react";

// SPRINT 04 · V2.1 Bild-Mapping — pixelweise verifiziert.
// Ordnung: Protection, Green, Water, Solar, Smart, Media, Mobility, Lounge, Art
const IMGS = [
  // 0 PROTECTION — ikonischer matte black cube mit gold LED (Signature-Bild)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/augxq3h3_grafik.png",
  // 1 GREEN — Lavendel-Cube mit LED-Underglow bei Dämmerung
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/4oaa7yqu_grafik.png",
  // 2 WATER — Nebel-Cube mit spielenden Kindern
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/l6oa67br_grafik.png",
  // 3 SOLAR — Solar-Blütentrichter mit Wasserauffang auf Lavendel-Cube (NEU 04.02.2026)
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/jqb59oj9_grafik.png",
  // 4 SMART — Info-Stele mit Berliner Dom
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/zd1ocp1m_grafik.png",
  // 5 MEDIA — Digitale Werbeflächen mit Gedächtniskirche
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/mhen4h2q_grafik.png",
  // 6 MOBILITY — E-Scooter-Ladesäule mit Fahrzeugen
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/ya5febha_grafik.png",
  // 7 LOUNGE — Paar auf Bank zwischen Lavendel-Cubes bei Dämmerung
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/ugmpwqqf_grafik.png",
  // 8 ART — Cube mit abstraktem Kunstwerk auf digitalen Screens bei Nacht
  "https://customer-assets.emergentagent.com/job_stadt-zukunft-heute/artifacts/npa0fhck_grafik.png",
];

export default function Collection() {
  const { t } = useLang();
  return (
    <section
      id="collection"
      data-testid="section-collection"
      className="relative bg-[#16191D] py-32 md:py-48"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-14">
        <div className="grid lg:grid-cols-12 gap-8 mb-10 md:mb-14">
          <div className="lg:col-span-7">
            <Reveal>
              <Kicker>{t.collection.kicker}</Kicker>
              <p
                data-testid="collection-line"
                className="mb-5 md:mb-7 font-display font-light tracking-[-0.01em] text-white/75 text-base md:text-lg"
              >
                {t.collection.line}
              </p>
              <SectionTitle className="!text-[clamp(2.75rem,7vw,6.5rem)]">
                {t.collection.title}
              </SectionTitle>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Reveal delay={0.1}>
              <p className="text-white/55 text-base leading-relaxed">{t.collection.sub}</p>
            </Reveal>
          </div>
        </div>

        {/* Kompakte Plattform-Einleitung (ersetzt frühere Sektionen 'The Line' + 'Die Plattform') */}
        <Reveal delay={0.15}>
          <div
            data-testid="collection-platform-intro"
            className="mb-14 md:mb-20 grid lg:grid-cols-12 gap-8 items-start border-t border-white/8 pt-10 md:pt-14"
          >
            <p className="lg:col-span-8 text-white/70 text-base md:text-[17px] leading-[1.7] max-w-3xl">
              {t.collection.intro}
            </p>
            <div className="lg:col-span-4 lg:col-start-9 flex flex-wrap gap-2 lg:justify-end">
              {t.collection.chips.map((c, i) => (
                <span
                  key={c}
                  data-testid={`collection-chip-${i}`}
                  translate="no"
                  className="notranslate inline-flex items-center gap-2 px-3 py-1.5 border border-white/15 text-[10px] tracking-[0.3em] uppercase text-white/70 font-display"
                >
                  <span className="h-1 w-1 rounded-full bg-[#F5D77A]" />
                  {c}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        {/* SPRINT 04 · Große Karten — 2-column premium grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {t.collection.items.map((it, i) => (
            <Reveal key={it.t} delay={i * 0.04}>
              <a
                href="#cta"
                data-testid={`collection-card-${i}`}
                className="group relative block overflow-hidden border border-white/8 hover:border-[#F5D77A]/50 transition-colors bg-[#1F2328]"
              >
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={IMGS[i]}
                    alt={it.t}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-[1.06]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1F2328]/60 via-transparent to-transparent" />
                  <div translate="no" className="notranslate absolute top-6 left-6 flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-white/85 font-display">
                    <span className="h-px w-6 bg-[#F5D77A]" />
                    Cube · {String(i + 1).padStart(2, "0")}
                  </div>
                </div>

                {/* Text block */}
                <div className="relative flex items-end justify-between gap-8 p-8 md:p-10">
                  <div className="flex-1">
                    <h3
                      translate="no"
                      className="notranslate font-display font-light tracking-[-0.02em] leading-[1] text-white"
                      style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}
                    >
                      {it.t}
                    </h3>
                    <p className="mt-4 max-w-md text-[14.5px] leading-relaxed text-white/55">
                      {it.d}
                    </p>
                    <div className="mt-6 h-px w-8 bg-[#F5D77A]/50 group-hover:w-24 transition-all duration-700" />
                  </div>
                  <ArrowUpRight
                    className="h-6 w-6 text-white/40 group-hover:text-[#F5D77A] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all shrink-0"
                    strokeWidth={1.5}
                  />
                </div>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
